import { Component, DetailEvent, Entity, registerComponent } from "aframe";
import { LocationSystem } from "../location";
import { Guid, parseGuid } from "../../rtc/guid";
import { AvatarSystem } from "../avatar";
import { waitForEntityLocation } from "../offline/offline-gateway";
import { waitForLoaded, waitForParentsLoaded, whenLoaded } from "../utils";
import { RtcChannelComponent, RtcChannelEntity, RtcChannelSystem } from "./rtc-channel";


interface RtcGatewayData
{
	name : string;
	channel : Guid;
	target : string;
	joined : string;
};
interface RtcGatewayComponent extends Component
{
	data : RtcGatewayData;
	joinedLeftListener : EventListener;
}

registerComponent("rtc-gateway", {
	schema : {
		name : { type : "string" },
		channel : { 
			default: Guid.empty,
			parse: parseGuid,
			stringify: (v : any) => v && v.toString()
		},
		target : { type : "string" }, //Name of the gateway in channel where to teleport on join
		joined : { type : "string", default : "" }
	},

	init(this : RtcGatewayComponent)
	{
		const scene = this.el.sceneEl!;
		this.joinedLeftListener = ((event: DetailEvent<RtcChannelComponent>) =>
		{
			if(event.detail.name != "rtc-channel")
				return;
			if(event.detail.data.id.equals(this.data.channel))
				this.el.setAttribute(this.attrName!, "joined", String( event.type == 'channel-joined' ) ); //Yay : reflect state!
		}) as EventListener;
		scene.addEventListener("channel-joined", this.joinedLeftListener, { capture : true });
		scene.addEventListener("channel-left", this.joinedLeftListener, { capture : true });
	},


	update(this : RtcGatewayComponent, oldData : RtcGatewayData)
	{
		if(this.data.name)
			this.el.dataset.gatewayName = this.data.name;
		else
			delete this.el.dataset.gatewayName;

		if(this.data.name && oldData && (this.data.name != oldData.name))
			this.el.emit('gateway-initialized', this.data.name);

		const targetChannelId = this.data.channel;
		if(!targetChannelId) return; //Target channel not configured yet.

		const scene = this.el.sceneEl!;
		const system = scene.systems["rtc-channel"] as RtcChannelSystem;
		if(oldData.channel && !oldData.channel.equals(targetChannelId)) //Config changed
		{
			this.data.joined = "";
		}

		if(!this.data.joined)
		{
			const channelEl = system.getChannel(targetChannelId);
			channelEl && whenLoaded(channelEl, (_) => {
				const channelComp = channelEl.components["rtc-channel"];
				// Initialize "joined" attribute with whether or not the channel is actually joined :
				if(channelComp && !this.data.joined)
					this.el.setAttribute(this.attrName!, {joined : String(channelComp.joined) });
			} );
	
			return; //Not wanting anything
		}

		

		
		const channelEl = system.getChannel(targetChannelId);
		const channelComp = channelEl && channelEl.components["rtc-channel"];

		const shouldJoin : boolean = JSON.parse(this.data.joined);
		if( channelComp && (channelComp.joined == shouldJoin))
			return; //OK!

		if(!shouldJoin) 
		{
			//Need to leave that channel
			if(channelEl)
				waitForLoaded(channelEl).then( (e) => e.emit("leave-channel") );
			return;
		}
		
		//Need to join that channel
		const joinEvent = new CustomEvent("join-channel", {
			detail : this.data.channel,
			cancelable : true,
			bubbles : true
		});

		this.el.dispatchEvent(joinEvent);

		if(!joinEvent.defaultPrevented) //Event was not handled <=> channel is not being joined.
		{
			this.data.joined = String(false);
			return;
		}



		const targetGatewayName = this.data.target;

		(this.el.sceneEl!.systems.location as LocationSystem).addChannel( 'rtc:' + String(this.data.channel), targetGatewayName );

		if(!targetGatewayName)
			return; //Nothing more to do.
			
		
			
		const avatarEl = (scene.systems.avatar as AvatarSystem).avatar;
		//Transfer physics responsibility to avatar itself :
		//if(avatarEl.components["ammo-body"]) //Disable avatar physics
			//avatarEl.setAttribute("ammo-body", { enabled : false });
		avatarEl.emit("enter-gateway", this);

		//We need to wait for parent channel to be loaded. If we don't, it won't listen to the event.
		waitForParentsLoaded(this.el).then( (e) => e.emit("leave-channel") );

		//Now, wait to join the channel, find the target gateway, and teleport :
		const teleportTo = async (el : Entity) =>
		{
			await waitForEntityLocation(el);

			el.emit("teleport-avatar");

			avatarEl.emit("exit-gateway", this);
			
			//if(avatarEl.components["ammo-body"])
				//avatarEl.setAttribute("ammo-body", { enabled : true });
		};


		const joinedListener = ((event : DetailEvent<RtcChannelComponent>) =>
		{
			if(event.detail.name != "rtc-channel")
				return;

			if(!event.detail.data.id.equals(targetChannelId))
				return;

			const channelEl = event.target as RtcChannelEntity;

			scene.removeEventListener("channel-joined", joinedListener, { capture : true } );

			
			const existingGateways = channelEl.querySelectorAll<Entity>('[rtc-gateway],[offline-gateway]');
			for(let n=existingGateways.length; --n >= 0; )
			{
				const gw = existingGateways[n];
				const gwComp = ( gw.components["rtc-gateway"] || gw.components["offline-gateway"] ) as Component & { data : { name? : string }};
				if(gwComp?.data?.name == targetGatewayName)
				{
					console.log("Teleporting to existing gateway :", targetGatewayName);
					return teleportTo(gw);
				}
			}

			console.log("Waiting for gateway :", targetGatewayName);
			//If target gateway doens't exist yet, wait for it to emit an event :
			const gatewayListener = ((event : DetailEvent<string>) =>
			{
				console.log("gateway-initialized :", event.detail, "( looking for", targetGatewayName , ")");
				if(event.detail != targetGatewayName) return;
				channelEl.removeEventListener("gateway-initialized", gatewayListener );
				teleportTo(event.target);
			}) as EventListener;
			channelEl.addEventListener("gateway-initialized", gatewayListener);
		} ) as EventListener;

		scene.addEventListener("channel-joined", joinedListener, { capture : true } );
	},

	remove(this: RtcGatewayComponent)
	{
		const scene = this.el.sceneEl!;
		scene.removeEventListener("channel-joined", this.joinedLeftListener, { capture : true });
		scene.removeEventListener("channel-left", this.joinedLeftListener, { capture : true });
	}

} );
